import { keyName } from "../../ui/input/router.ts";
import { ACTIONS, ACTION_IDS, HELD_ACTIONS, type ActionId } from "./actions.ts";
import { codeShaped } from "./key-shape.ts";
import { US_LAYOUT } from "./layout.ts";
import { reservedCode } from "./reserved.ts";

/**
 * The key table as pure data (P1.9-T50; ADR-061's amendments "key rebinding", decisions 6, 7 and 9, and "every key
 * rebindable but Escape", points 1, 2, 5 and 7): which physical key (`KeyboardEvent.code`) each action has, the action
 * a press is, the clash rule Apply follows, and how the table is kept. Nothing a key types is matched: a press is its
 * physical key, so every binding keeps its place on every layout and Shift changes nothing it means.
 */

/**
 * Each action's physical key.
 */
export type Bindings = Readonly<Record<ActionId, string>>;

/**
 * Each action's physical key before a player chooses.
 */
export const DEFAULT_BINDINGS: Bindings = Object.freeze(Object.fromEntries(ACTIONS.map((action) => [action.id, action.code])) as Record<ActionId, string>);

/**
 * The physical keys that answer for another where no action holds them (point 2): the numpad's digits for the digit
 * row (ADR-116 decision 10), the numpad's Enter for Enter, and each right modifier for the left.
 */
const ALIASES: Readonly<Record<string, string>> = Object.freeze({
  ...Object.fromEntries(Array.from({ length: 10 }, (_, digit) => [`Numpad${digit}`, `Digit${digit}`])),
  NumpadEnter: "Enter",
  ShiftRight: "ShiftLeft",
  ControlRight: "ControlLeft",
  AltRight: "AltLeft",
  MetaRight: "MetaLeft",
});

/**
 * The physical key of a press whose event names none, by the name it types: the key the US layout types it on, and the
 * words a named key is, Space and the left modifiers among them.
 */
const NAMED_CODES: Readonly<Record<string, string>> = Object.freeze({ " ": "Space", Shift: "ShiftLeft", Control: "ControlLeft", Alt: "AltLeft", Meta: "MetaLeft" });

/**
 * A press's physical key: its code, or, for an event that names none, the code its character is on with a US layout.
 *
 * @param press The press's `key` and, when the event says, its `code`.
 * @returns The code, or null when neither says.
 */
export function codeOfPress(press: { readonly key: string; readonly code?: string | undefined }): string | null {
  if (press.code !== undefined && press.code !== "") {
    return press.code;
  }
  const name = keyName(press.key);
  return NAMED_CODES[name] ?? US_LAYOUT.codeOf(name);
}

/**
 * The action bound to a physical key.
 *
 * @param bindings The table.
 * @param code The code.
 * @returns The action, or null when none holds it.
 */
function holderOf(bindings: Bindings, code: string): ActionId | null {
  for (const id of ACTION_IDS) {
    if (bindings[id] === code) {
      return id;
    }
  }
  return null;
}

/**
 * The action a press is (point 2): the action on its physical key, else the action on the key it is an alias of.
 *
 * @param bindings The table.
 * @param press The press's `key` and, when the event says, its `code`.
 * @returns The action, or null when the press is none.
 * @example
 * actionOfPress(DEFAULT_BINDINGS, { key: "z", code: "KeyW" });
 * // "moveUp": AZERTY's Z key is where W is
 */
export function actionOfPress(bindings: Bindings, press: { readonly key: string; readonly code?: string | undefined }): ActionId | null {
  const code = codeOfPress(press);
  if (code === null) {
    return null;
  }
  const own = holderOf(bindings, code);
  if (own !== null) {
    return own;
  }
  const alias = ALIASES[code];
  return alias === undefined ? null : holderOf(bindings, alias);
}

/**
 * Whether an action may hold a physical key: a held action never Tab (point 5).
 *
 * @param action The action.
 * @param code The code.
 * @returns True when it suits.
 */
function suits(action: ActionId, code: string): boolean {
  return code !== "Tab" || !HELD_ACTIONS.includes(action);
}

/**
 * Chooses a key for an action in the pending table (decision 7). The key may be one an unchanged action holds, which
 * pends twice until Apply and is a clash; another changed action that pends the same key returns to its key in force,
 * so the newer choice stands and two changed actions never pend one key.
 *
 * @param applied The table in force.
 * @param pending The pending table.
 * @param action The action.
 * @param code The key chosen.
 * @returns The pending table with the choice, or the same table when the action already pends that key.
 * @example
 * chooseKey(DEFAULT_BINDINGS, DEFAULT_BINDINGS, "interact", "KeyF").interact;
 * // "KeyF"
 */
export function chooseKey(applied: Bindings, pending: Bindings, action: ActionId, code: string): Bindings {
  if (pending[action] === code) {
    return pending;
  }
  const next: Record<ActionId, string> = { ...pending, [action]: code };
  for (const other of ACTION_IDS) {
    if (other !== action && next[other] !== applied[other] && next[other] === code) {
      next[other] = applied[other];
    }
  }
  return next;
}

/**
 * The key each changed action takes in a pending table, the first changed action in the table's order winning a key
 * two pend.
 *
 * @param applied The table in force.
 * @param pending The pending table.
 * @returns Each key taken and the action that takes it.
 */
function takers(applied: Bindings, pending: Bindings): ReadonlyMap<string, ActionId> {
  const taken = new Map<string, ActionId>();
  for (const id of ACTION_IDS) {
    if (pending[id] !== applied[id] && !taken.has(pending[id])) {
      taken.set(pending[id], id);
    }
  }
  return taken;
}

/**
 * A clash: a changed action, the key it pends, and the unchanged action that holds that key in force.
 */
export interface Clash {
  readonly action: ActionId;
  readonly code: string;
  readonly holder: ActionId;
}

/**
 * The clashes of a pending table, in the table's order of the actions that hold the keys.
 *
 * @param applied The table in force.
 * @param pending The pending table.
 * @returns The clashes; none while every key is held once.
 * @example
 * clashesOf(DEFAULT_BINDINGS, { ...DEFAULT_BINDINGS, interact: "Space" });
 * // [{ action: "interact", code: "Space", holder: "jump" }]
 */
export function clashesOf(applied: Bindings, pending: Bindings): readonly Clash[] {
  const taken = takers(applied, pending);
  const clashes: Clash[] = [];
  for (const holder of ACTION_IDS) {
    const taker = taken.get(applied[holder]);
    if (taker !== undefined && taker !== holder && taken.get(pending[holder]) !== holder) {
      clashes.push({ action: taker, code: applied[holder], holder });
    }
  }
  return clashes;
}

/**
 * Apply's rule (decision 7): each changed action takes its pending key; an unchanged action whose key was taken takes
 * the key its taker gave up when that key is free, and otherwise the first key given up that no action holds, in the
 * table's order. A table in force that holds no key twice gives one that holds none twice either, with every action
 * on a key.
 *
 * @param applied The table in force.
 * @param pending The pending table, which may hold a key twice.
 * @returns The table Apply puts in force.
 * @example
 * resolveBindings(DEFAULT_BINDINGS, { ...DEFAULT_BINDINGS, interact: "Space" }).jump;
 * // "KeyE"
 */
export function resolveBindings(applied: Bindings, pending: Bindings): Bindings {
  const taken = takers(applied, pending);
  if (taken.size === 0) {
    return applied;
  }
  const resolved: Record<ActionId, string> = { ...applied };
  const held = new Set<string>();
  const displaced: ActionId[] = [];
  for (const id of ACTION_IDS) {
    if (taken.get(pending[id]) === id) {
      resolved[id] = pending[id];
      held.add(pending[id]);
    } else if (taken.has(applied[id])) {
      displaced.push(id);
    } else {
      held.add(applied[id]);
    }
  }
  const free = [...taken.values()].map((taker) => applied[taker]).filter((code) => !held.has(code));
  for (const id of displaced) {
    const wanted = applied[taken.get(applied[id]) as ActionId];
    const at = Math.max(0, free.indexOf(wanted));
    const code = free[at];
    if (code !== undefined) {
      free.splice(at, 1);
      resolved[id] = code;
    }
  }
  return resolved;
}

/**
 * Why a choice is refused for what Apply would make of it: the action the swap would leave on a key it cannot hold, a
 * held action on Tab, with that key; null when the choice stands.
 *
 * @param applied The table in force.
 * @param pending The pending table.
 * @param action The action a key is chosen for.
 * @param code The key.
 * @returns The action left on a key that does not suit it, with that key, or null.
 * @example
 * refusalOf(DEFAULT_BINDINGS, DEFAULT_BINDINGS, "target", "KeyW");
 * // { action: "moveUp", code: "Tab" }
 */
export function refusalOf(applied: Bindings, pending: Bindings, action: ActionId, code: string): { readonly action: ActionId; readonly code: string } | null {
  const resolved = resolveBindings(applied, chooseKey(applied, pending, action, code));
  const left = ACTION_IDS.find((id) => !suits(id, resolved[id]));
  return left === undefined ? null : { action: left, code: resolved[left] };
}

/**
 * The longest stored text read as a table: every action on a code of the longest shape fits in it. Anything longer was
 * not written by the game and reads as the defaults, unparsed.
 */
export const MAX_STORED_LENGTH = 4096;

/**
 * Reads the table a device kept (decision 9, point 7): each changed action's code, taken over the defaults by Apply's
 * rule, so the table read is whole and holds no key twice whatever was stored. An unknown action, a code that is not
 * one or cannot be bound, and a held action on Tab are dropped; a text longer than `MAX_STORED_LENGTH`, anything that is
 * not an object, and a table in which any action's entry is not a string (the `{key, code}` entries kept before) read
 * as the defaults, as does a table of characters, whose entries are no codes.
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns The table.
 * @example
 * parseBindings('{"interact":"KeyF"}').interact;
 * // "KeyF"
 */
export function parseBindings(raw: string | null): Bindings {
  if (raw === null || raw.length > MAX_STORED_LENGTH) {
    return DEFAULT_BINDINGS;
  }
  let stored: unknown;
  try {
    stored = JSON.parse(raw);
  } catch {
    return DEFAULT_BINDINGS;
  }
  if (typeof stored !== "object" || stored === null || Array.isArray(stored)) {
    return DEFAULT_BINDINGS;
  }
  const entries = stored as Record<string, unknown>;
  const kept = ACTION_IDS.filter((id) => Object.hasOwn(entries, id));
  if (kept.some((id) => typeof entries[id] !== "string" || !codeShaped(entries[id] as string))) {
    return DEFAULT_BINDINGS;
  }
  const pending: Record<ActionId, string> = { ...DEFAULT_BINDINGS };
  for (const id of kept) {
    const code = entries[id] as string;
    if (reservedCode(code) === null && suits(id, code)) {
      pending[id] = code;
    }
  }
  const resolved = resolveBindings(DEFAULT_BINDINGS, pending);
  return ACTION_IDS.every((id) => suits(id, resolved[id])) ? resolved : DEFAULT_BINDINGS;
}

/**
 * The table as it is kept: a JSON object of the actions whose key is not the default, each its code, in the table's
 * order.
 *
 * @param bindings The table.
 * @returns The text to store.
 * @example
 * formatBindings({ ...DEFAULT_BINDINGS, interact: "KeyF" });
 * // '{"interact":"KeyF"}'
 */
export function formatBindings(bindings: Bindings): string {
  return JSON.stringify(Object.fromEntries(ACTION_IDS.filter((id) => bindings[id] !== DEFAULT_BINDINGS[id]).map((id) => [id, bindings[id]])));
}

/**
 * Whether two tables bind every action to the same key.
 *
 * @param a One table.
 * @param b The other.
 * @returns True when they agree on every action.
 */
export function sameBindings(a: Bindings, b: Bindings): boolean {
  return a === b || ACTION_IDS.every((id) => a[id] === b[id]);
}
